<template>
  <div class="week-nav-header">
    <div class="week-nav-main">
      <div class="week-nav-buttons">
        <b-button
          variant="outline-secondary"
          class="week-nav-arrow"
          @click="$emit('prev')"
        >
          <b-icon-chevron-left />
        </b-button>
        <b-button
          variant="outline-secondary"
          class="week-nav-arrow"
          @click="$emit('next')"
        >
          <b-icon-chevron-right />
        </b-button>
      </div>
      <b-button
        variant="outline-primary"
        size="sm"
        class="week-today-btn"
        @click="$emit('today')"
      >
        Today
      </b-button>
      <h5 class="week-label">{{ weekLabel }}</h5>
    </div>

    <div class="week-nav-filters">
      <b-input-group class="week-search">
        <template #prepend>
          <b-input-group-text><b-icon-search /></b-input-group-text>
        </template>
        <b-form-input
          :value="search"
          placeholder="Search tradie..."
          @input="$emit('search', $event)"
        />
      </b-input-group>

      <b-form-select
        :value="value"
        :options="projectOptions"
        class="week-project-filter"
        @input="$emit('input', $event)"
      />

      <div class="week-start-day">
        <label class="week-start-day-label">Week starts on</label>
        <b-form-select
          :value="startDay"
          :options="startDayOptions"
          class="week-start-day-select"
          @change="$emit('start-day-change', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WeekNavHeader',

  props: {
    weekLabel: {
      type: String,
      default: '',
    },
    projectOptions: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [Number, String],
      default: null,
    },
    search: {
      type: String,
      default: '',
    },
    startDay: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      startDayOptions: [
        { value: 0, text: 'Sunday' },
        { value: 1, text: 'Monday' },
        { value: 2, text: 'Tuesday' },
        { value: 3, text: 'Wednesday' },
        { value: 4, text: 'Thursday' },
        { value: 5, text: 'Friday' },
        { value: 6, text: 'Saturday' },
      ],
    }
  },
}
</script>

<style scoped>
.week-nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
}

.week-nav-main,
.week-nav-filters {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}

.week-nav-buttons {
  display: inline-flex;
  gap: 0.35rem;
}

.week-nav-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
}

.week-today-btn {
  height: 36px;
  padding-right: 0.85rem;
  padding-left: 0.85rem;
  border-radius: 8px;
  font-weight: 600;
}

.week-label {
  margin: 0 0 0 0.25rem;
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
}

.week-search,
.week-project-filter {
  width: 220px;
}

.week-start-day {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.week-start-day-label {
  margin: 0;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 600;
}

.week-start-day-select {
  width: 130px;
  height: 38px;
  border-color: #d1d5db;
  border-radius: 8px;
}

.week-search /deep/ .input-group-text,
.week-search /deep/ .form-control,
.week-project-filter {
  height: 38px;
  border-color: #d1d5db;
}

.week-search /deep/ .input-group-text {
  background: #ffffff;
  color: #6b7280;
  border-radius: 8px 0 0 8px;
}

.week-search /deep/ .form-control {
  border-radius: 0 8px 8px 0;
}

.week-project-filter {
  border-radius: 8px;
}

/* Below xl the single row can't fit (arrows + Today + week label + the three
   filters need ~1130px) — stack into two rows instead of overlapping. Matches
   the roster grid's own card/table switch at 1200px. */
@media (max-width: 1199.98px) {
  .week-nav-header {
    align-items: stretch;
    flex-direction: column;
  }

  .week-nav-main,
  .week-nav-filters {
    flex-wrap: wrap;
  }

  .week-search,
  .week-project-filter {
    flex: 1 1 220px;
    width: auto;
  }
}

/* Phones: a tidy vertical layout instead of a compressed jumble —
   arrows + Today share one line, the week label gets its own, and each
   filter spans the full width with even spacing. */
@media (max-width: 575px) {
  .week-nav-header {
    gap: 0.75rem;
    padding: 0.85rem;
  }

  .week-nav-main {
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .week-label {
    flex: 0 0 100%;
    margin: 0.15rem 0 0;
    font-size: 0.95rem;
    white-space: normal;
  }

  .week-nav-filters {
    align-items: stretch;
    flex-direction: column;
    gap: 0.6rem;
  }

  .week-search,
  .week-project-filter {
    flex: none;
    width: 100%;
  }

  .week-start-day {
    justify-content: space-between;
  }

  .week-start-day-select {
    flex: 1 1 auto;
    width: auto;
    max-width: 65%;
  }
}
</style>
